<template>
  <div>
    <v-dialog v-model="show" persistent max-width="40%">
      <v-card class="elevation-12">
        <v-app-bar dark color="primary">
          <v-toolbar-title>{{ title || $t("auth.register_person") }}</v-toolbar-title>
        </v-app-bar>
        <v-card-text class="pa-5">
          <v-layout row wrap>
            <v-flex lg4 md4 sm6 xs12>
              <v-text-field
                v-model="person.first_name"
                v-validate="'required|max:50'"
                class="required-field"
                :label="$t('users.first_name')"
                :error-messages="errors.collect('person.first_name')"
                data-vv-name="person.first_name"
                autocomplete="new-password"
              ></v-text-field>
            </v-flex>
            <v-flex lg4 md4 sm6 xs12>
              <v-text-field
                v-model="person.middle_name"
                v-validate="'max:50'"
                :label="$t('users.middle_name')"
                :error-messages="errors.collect('person.middle_name')"
                data-vv-name="person.middle_name"
                autocomplete="new-password"
              ></v-text-field>
            </v-flex>
            <v-flex lg4 md4 sm6 xs12>
              <v-text-field
                v-model="person.last_name"
                v-validate="'required|max:50'"
                class="required-field"
                :label="$t('users.last_name')"
                :error-messages="errors.collect('person.last_name')"
                data-vv-name="person.last_name"
                autocomplete="new-password"
              ></v-text-field>
            </v-flex>
            <v-flex lg4 md4 sm6 xs12>
              <vue-tel-input-vuetify
                class="required-field"
                v-validate="'required|max:20'"
                :label="$t('users.mobile')"
                v-model="person.mobile"
                @input="
                  (formattedNumber, { number, valid, country }) => {
                    person.mobile = number.significant || formattedNumber
                    person.mobile_code = country.dialCode || null
                  }
                "
                placeholder=""
                data-vv-name="person.mobile"
                :error-messages="errors.collect('person.mobile')"
                defaultCountry="NO"
              ></vue-tel-input-vuetify>
            </v-flex>
            <v-flex lg4 md4 sm6 xs12>
              <v-text-field
                v-model="user.email"
                v-validate="'required|max:255'"
                :label="$t('users.email')"
                :error-messages="errors.collect('user.email')"
                data-vv-name="user.email"
                autocomplete="new-password"
              ></v-text-field>
            </v-flex>
            <v-flex lg4 md4 sm6 xs12>
              <v-dialog ref="dialog" v-model="dob_date_menu" persistent width="290px">
                <template v-slot:activator="{ on }">
                  <v-text-field
                    name="person.date_of_birth"
                    v-model="computedDateofBirth"
                    :label="$t('persons.date_of_birth')"
                    class="required-field"
                    v-validate="'required|validDate'"
                    :error-messages="errors.collect('person.date_of_birth')"
                    append-icon="event"
                    @click:append="dob_date_menu = true"
                    :hint="'Format: ' + DATE_FORMAT"
                    autocomplete="new-password"
                  ></v-text-field>
                </template>
                <v-date-picker
                  color="primary"
                  v-model="person.date_of_birth"
                  first-day-of-week="1"
                  :locale="$locale_bcp47(locale)"
                  :max="moment().subtract(1, 'days').format(MYSQL_DATE_FORMAT)"
                >
                  <v-spacer></v-spacer>
                  <v-btn text color="primary" @click="dob_date_menu = false" class="darken-2">{{ $t("global.ok") }}</v-btn>
                </v-date-picker>
              </v-dialog>
            </v-flex>
            <v-flex xs12 class="text-center">
              <v-checkbox dense v-model="accept_privacy" v-validate="'required'" class="d-inline-flex"> </v-checkbox>
              <p class="d-inline-flex va-top privacy-line">
                {{ $t("auth.privacy_consent_to") }}
                <a @click="show_policy = !show_policy" class="pl-1 privacy-link">{{ $t("auth.privacy_notice") }}</a>
              </p>
            </v-flex>
          </v-layout>
        </v-card-text>
        <v-card-actions class="pa-3">
          <v-spacer></v-spacer>

          <action-button @click="generateCode" type="default" :text="$t('global.submit')" icon="" :disabled="!accept_privacy" />
          <action-button @click="cancel" type="default" :text="$t('global.back')" icon="" />
        </v-card-actions>
      </v-card>
    </v-dialog>

    <codePopup
      :show="showCodePopup"
      :uuid="uuid"
      :locale="locale"
      @callback_codepopup_cancel="callback_codepopup_cancel"
      @callback_codepopup_register="callback_codepopup_register"
    />

    <privacyPolicy :show="show_policy" :locale="locale" @callback_privacy_cancel="show_policy = false" />
  </div>
</template>

<script>
import codePopup from "./CodePopup.vue"
import privacyPolicy from "./Privacy.vue"
import PhoneNumber from "awesome-phonenumber"
import VueTelInputVuetify from "@/mixins/vue-tel-input-vuetify/lib/vue-tel-input-vuetify.vue"

export default {
  name: "register-user-popup",

  components: {
    codePopup,
    privacyPolicy,
    VueTelInputVuetify
  },

  props: {
    show: {
      type: Boolean,
      required: true
    },
    locale: {
      type: String
    },
    title: {
      type: String
    },
    pre_firstname: {
      type: String
    },
    pre_lastname: {
      type: String
    },
    pre_email: {
      type: String
    },
    pre_feide_username: {
      type: String
    },
    pre_mobile: {
      type: String
    },
    pre_date_of_birth: {
      type: String
    }
  },

  data() {
    return {
      person: {},
      user: {},

      dob_date_menu: false,

      uuid: "",
      showCodePopup: false,
      password: "",

      show_policy: false,
      accept_privacy: false,

      dictionary: {
        attributes: {
          "user.email": this.$t("users.email"),
          "user.password": this.$t("users.password"),
          "user.status": this.$t("users.status"),

          "person.first_name": this.$t("users.first_name"),
          "person.middle_name": this.$t("users.middle_name"),
          "person.last_name": this.$t("users.last_name"),
          "person.mobile": this.$t("users.mobile"),
          "person.date_of_birth": this.$t("persons.date_of_birth"),
          "person.comments": this.$t("users.comments")
        }
      }
    }
  },

  mounted() {
    this.$validator.localize(this.locale, this.dictionary)

    if (this.pre_firstname) this.$set(this.person, "first_name", this.pre_firstname)
    if (this.pre_lastname) this.$set(this.person, "last_name", this.pre_lastname)
    if (this.pre_email) this.$set(this.user, "email", this.pre_email)
    if (this.pre_feide_username) this.$set(this.user, "feide_username", this.pre_feide_username)
    if (this.pre_mobile) this.$set(this.person, "mobile", this.pre_mobile)
    if (this.pre_date_of_birth) this.$set(this.person, "date_of_birth", this.pre_date_of_birth)
  },

  created() {
    let vm = this
    let user = localStorage.getItem("__crrntUser")

    this.uuid = this.$generateUUID()
    this.person.mobile_code = "47"

    this.$updateSetting("fe_locale", this.locale)
    this.$i18n.locale = this.locale

    if (user) {
      vm.$router.push(this.BACKEND_URL + "/dashboard")
    }
  },

  computed: {
    isFormValid() {
      return !Object.keys(this.fields).some((key) => this.fields[key].invalid)
    },

    computedDateofBirth: {
      get() {
        return this.$dateFormatGetter(this.person.date_of_birth)
      },
      set(val) {
        this.$set(this.person, "date_of_birth", this.$dateFormatSetter(val))
      }
    }
  },

  methods: {
    generateCode() {
      let vm = this
      this.$validator.validate()

      this.$validator.validateAll().then(() => {
        if (this.isFormValid && this.uuid) {
          axios
            .post(this.API_URL + "auth/validate", {
              email: vm.user.email,
              mobile: vm.$formatPhoneNumber(vm.person.mobile, vm.person.mobile_code)
            })
            .then((res) => {
              if (res.data.status) {
                axios
                  .post(this.API_URL + "auth/generatecode", {
                    uuid: this.uuid,
                    email: vm.user.email,
                    first_name: vm.person.first_name,
                    last_name: vm.person.last_name
                  })
                  .then((res) => {
                    if (res.data.status) {
                      this.showCodePopup = true
                    }
                  })
                  .catch((err) => console.log(err))
              } else {
                vm.$awn.alert(res.data.error)
              }
            })
            .catch((err) => console.log(err))
        } else {
          this.$awn.alert(this.$t("global.form_not_valid"))
        }
      })
    },

    register() {
      let vm = this
      this.$validator.validate()

      this.$validator.validateAll().then(() => {
        if (this.isFormValid) {
          axios
            .post(this.API_URL + "auth/register/user", {
              type: "user",
              email: vm.user.email,
              feide_username: vm.user.feide_username,
              password: vm.password,
              mobile: vm.$formatPhoneNumber(vm.person.mobile, vm.person.mobile_code),
              person: {
                first_name: vm.person.first_name,
                middle_name: vm.person.middle_name,
                last_name: vm.person.last_name,
                mobile: vm.$formatPhoneNumber(vm.person.mobile, vm.person.mobile_code),
                date_of_birth: vm.person.date_of_birth
              }
            })
            .then((res) => {
              if (res.data.status) {
                vm.$emit("callback_register_result", {
                  status: true,
                  id: res.data.data.id,
                  login_token: res.data.login_token
                })
              } else {
                vm.$emit("callback_register_result", {
                  status: false
                })
              }
            })
            .catch((err) => console.log(err))
        } else {
          this.$awn.alert(this.$t("global.form_not_valid"))
        }
      })
    },
    cancel() {
      this.$emit("callback_register_cancel")
    },
    callback_codepopup_register(password) {
      if (password) {
        this.password = password
        this.register()
      }

      this.showCodePopup = false
    },
    callback_codepopup_cancel() {
      this.showCodePopup = false
    },
    phoneNoSpace() {
      if (this.person.mobile) {
        this.person.mobile = this.person.mobile.replace(/\s/g, "")
        let field = this.errors.items.find((i) => i.field == "person.mobile" && i.rule == "no_space")
        if (field) this.$validator.errors.removeById(field.id)
      }
    }
  }
}
</script>
